import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  MessageSquare,
  Lock,
  Globe,
  Paperclip,
  X,
  ChevronLeft,
  Trash2,
  Send,
  Pencil,
} from "lucide-react";

// ──────────────────────────────────────────────
// InquiryBoardPage — 문의·건의 게시판 (loginHS)
//   목록 / 작성(비밀 기본) / 상세 스레드 / 관리자 답글.
//   첨부: 화면 캡처 Ctrl+V 붙여넣기 + 파일 선택. 비밀글은 본인·동일그룹·관리자만.
//   API: /api/inquiry (requireValidSession — 로그인 필수)
// ──────────────────────────────────────────────

const INK = "#16151a";
const DARK = "#1b1a20";
const LINE = "#ecedf2";
const MUTED = "#6b7280";

const token = () => localStorage.getItem("token");
const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

function fmtDate(d) {
  if (!d) return "";
  const t = new Date(d);
  return `${t.getFullYear()}.${String(t.getMonth() + 1).padStart(2, "0")}.${String(t.getDate()).padStart(2, "0")} ${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`;
}

// W2 품목 스레드 — 세번 표시(8504409019 → 8504.40-9019, hs6 → 8504.40)
function fmtTopicCode(code) {
  const s = String(code || "").replace(/\D/g, "");
  if (s.length < 6) return s;
  const head = `${s.slice(0, 4)}.${s.slice(4, 6)}`;
  return s.length > 6 ? `${head}-${s.slice(6)}` : head;
}

// 판정 결과 "이 분류에 의견 있음" 진입점이 넘긴 컨텍스트(10분 유효 · 1회 소모)
function takeInquiryPrefill() {
  try {
    const raw = localStorage.getItem("inquiry_prefill");
    localStorage.removeItem("inquiry_prefill");
    const p = raw ? JSON.parse(raw) : null;
    if (p && p.hs6 && Date.now() - (p.ts || 0) < 10 * 60 * 1000) return p;
  } catch (e) {
    /* 파싱 실패 = 프리필 없음 */
  }
  return null;
}

// 품목 스레드 배지(목록·상세 공용)
function TopicBadge({ hs6, hsk }) {
  if (!hs6 && !hsk) return null;
  return (
    <span
      style={{
        fontSize: 11.5,
        fontWeight: 700,
        padding: "1px 7px",
        borderRadius: 10,
        background: "#e8f0fe",
        color: "#1a56b0",
        whiteSpace: "nowrap",
        fontFamily: "'JetBrains Mono', monospace",
      }}
      title="품목 의견 스레드 — 판정 결과에서 제기된 의견입니다"
    >
      {fmtTopicCode(hsk || hs6)}
    </span>
  );
}

export default function InquiryBoardPage() {
  // 판정 결과에서 넘어온 경우 작성 화면으로 직행(집단지성 W2)
  const [prefill] = useState(takeInquiryPrefill);
  const [view, setView] = useState(prefill ? "write" : "list"); // 'list' | 'write' | 'thread'
  const [groupId, setGroupId] = useState(null);
  const isMobile = window.innerWidth < 768;

  return (
    <div
      style={{
        height: "100%",
        overflow: "auto",
        background: "#fafafa",
        fontFamily:
          "'Malgun Gothic', '맑은 고딕', 'Pretendard', -apple-system, 'Segoe UI', sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: 860,
          margin: "0 auto",
          padding: isMobile ? "16px 12px" : "24px 20px",
        }}
      >
        {view === "list" && (
          <InquiryList
            onWrite={() => setView("write")}
            onOpen={(gid) => {
              setGroupId(gid);
              setView("thread");
            }}
          />
        )}
        {view === "write" && (
          <InquiryWrite
            prefill={prefill}
            onCancel={() => setView("list")}
            onDone={() => setView("list")}
          />
        )}
        {view === "thread" && (
          <InquiryThread groupId={groupId} onBack={() => setView("list")} />
        )}
      </div>
    </div>
  );
}

// ─── 목록 ───────────────────────────────────────
function InquiryList({ onWrite, onOpen }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  useEffect(() => {
    fetch(`/api/inquiry?page=${page}&size=15`, { headers: authHeaders() })
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData({ items: [], total: 0 }));
  }, [page]);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 18 }}>
        <MessageSquare size={20} style={{ marginRight: 8 }} />
        <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0, color: INK }}>
          문의·건의
        </h1>
        <span style={{ fontSize: 12.5, color: MUTED, marginLeft: 10 }}>
          안 되는 점·건의사항을 남겨주세요
        </span>
        <button onClick={onWrite} style={{ ...primaryBtn, marginLeft: "auto" }}>
          새 문의
        </button>
      </div>

      <div style={card}>
        {!data ? (
          <Empty>불러오는 중…</Empty>
        ) : data.items.length === 0 ? (
          <Empty>아직 등록된 문의가 없습니다. 첫 문의를 남겨보세요.</Empty>
        ) : (
          data.items.map((it) => (
            <div
              key={it.id}
              onClick={() => it.viewable && onOpen(it.groupId)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "13px 16px",
                borderBottom: `1px solid ${LINE}`,
                cursor: it.viewable ? "pointer" : "default",
              }}
            >
              {it.isPublic ? (
                <Globe size={14} color={MUTED} />
              ) : (
                <Lock size={14} color={it.viewable ? "#b06000" : "#c0c4cc"} />
              )}
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: it.viewable ? INK : "#9ca3af",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {it.title}
                {it.isMine && <span style={miniTag}>내 글</span>}
              </span>
              {(it.hs6 || it.hsk) && <TopicBadge hs6={it.hs6} hsk={it.hsk} />}
              {it.status === "answered" && (
                <span
                  style={{
                    ...miniTag,
                    background: "#e6f4ea",
                    color: "#137333",
                  }}
                >
                  답변완료
                </span>
              )}
              <span
                style={{ fontSize: 12, color: MUTED, whiteSpace: "nowrap" }}
              >
                {it.author}
              </span>
              <span
                style={{
                  fontSize: 11.5,
                  color: "#9ca3af",
                  whiteSpace: "nowrap",
                  minWidth: 86,
                  textAlign: "right",
                }}
              >
                {fmtDate(it.createdAt).slice(0, 10)}
              </span>
            </div>
          ))
        )}
      </div>

      {data && data.total > 15 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 8,
            marginTop: 16,
          }}
        >
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            style={pageBtn}
          >
            이전
          </button>
          <span style={{ fontSize: 13, color: MUTED, alignSelf: "center" }}>
            {page} / {Math.ceil(data.total / 15)}
          </span>
          <button
            disabled={page >= Math.ceil(data.total / 15)}
            onClick={() => setPage((p) => p + 1)}
            style={pageBtn}
          >
            다음
          </button>
        </div>
      )}
    </div>
  );
}

// ─── 작성 ───────────────────────────────────────
function InquiryWrite({ prefill, onCancel, onDone }) {
  // W2 품목 의견: 판정 결과에서 진입하면 세번·품명이 미리 채워지고 공개가 기본
  // (집단지성 취지 — 공개 의견이 스레드에 쌓인다. 사용자가 해제 가능).
  const topic = prefill && prefill.hs6 ? prefill : null;
  const [title, setTitle] = useState(
    topic
      ? `[품목 의견] ${fmtTopicCode(topic.hsk || topic.hs6)}${topic.keyword ? ` — ${topic.keyword}` : ""}`
      : "",
  );
  const [content, setContent] = useState(
    topic
      ? `대상 세번: ${fmtTopicCode(topic.hsk || topic.hs6)}\n입력 품명: ${topic.keyword || "-"}\n\n의견/이의 내용:\n\n근거(사례·법령·실물 정보 등):\n`
      : "",
  );
  const [isPublic, setIsPublic] = useState(!!topic); // 비밀 기본(품목 의견은 공개 기본)
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const uploadFile = useCallback(async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch("/api/inquiry/upload", {
      method: "POST",
      headers: authHeaders(),
      body: fd,
    });
    const d = await r.json();
    if (d.success) setAttachments((prev) => [...prev, d]);
    else alert(d.error || "업로드 실패");
  }, []);

  // 화면 캡처 붙여넣기
  const onPaste = useCallback(
    (e) => {
      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.type.startsWith("image/")) {
          const f = it.getAsFile();
          if (f) uploadFile(f);
        }
      }
    },
    [uploadFile],
  );

  const submit = async () => {
    if (!title.trim()) return alert("제목을 입력해주세요.");
    if (!content.trim()) return alert("내용을 입력해주세요.");
    setBusy(true);
    try {
      const r = await fetch("/api/inquiry", {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          title,
          content,
          isPublic,
          attachments,
          // W2 품목 스레드 컨텍스트(있을 때만)
          hs6: topic ? topic.hs6 : undefined,
          hsk: topic ? topic.hsk : undefined,
          topicKeyword: topic ? topic.keyword : undefined,
        }),
      });
      const d = await r.json();
      if (d.success) onDone();
      else alert(d.error || "등록 실패");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
        <button onClick={onCancel} style={iconBtn}>
          <ChevronLeft size={18} />
        </button>
        <h1 style={{ fontSize: 18, fontWeight: 800, margin: 0, color: INK }}>
          {topic ? "품목 의견 남기기" : "새 문의"}
        </h1>
        {topic && (
          <span style={{ marginLeft: 8 }}>
            <TopicBadge hs6={topic.hs6} hsk={topic.hsk} />
          </span>
        )}
      </div>
      {topic && (
        <div
          style={{
            fontSize: 12.5,
            color: MUTED,
            margin: "-8px 0 12px",
            lineHeight: 1.5,
          }}
        >
          이 분류 결과에 대한 의견·반례는 검토 후 분류 기준(결정표) 개정에
          반영됩니다. 현재 답변은 잠정입니다.
        </div>
      )}

      <div style={{ ...card, padding: 18 }}>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="제목"
          style={input}
        />
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onPaste={onPaste}
          placeholder="내용을 입력하세요. 화면을 캡처해 Ctrl+V 로 바로 붙여넣을 수 있습니다."
          rows={9}
          style={{ ...input, resize: "vertical", lineHeight: 1.6 }}
        />

        {attachments.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              margin: "4px 0 10px",
            }}
          >
            {attachments.map((a, i) => (
              <div key={i} style={attChip}>
                {/image/.test(a.mime) ? (
                  <img
                    src={a.url}
                    alt=""
                    style={{
                      width: 54,
                      height: 54,
                      objectFit: "cover",
                      borderRadius: 6,
                    }}
                  />
                ) : (
                  <span style={{ fontSize: 12, padding: "0 6px" }}>
                    {a.name}
                  </span>
                )}
                <button
                  onClick={() =>
                    setAttachments((p) => p.filter((_, j) => j !== i))
                  }
                  style={attDel}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 6,
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              [...e.target.files].forEach(uploadFile);
              e.target.value = "";
            }}
          />
          <button onClick={() => fileRef.current?.click()} style={ghostBtn}>
            <Paperclip size={14} /> 파일 첨부
          </button>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              color: "#374151",
              cursor: "pointer",
              marginLeft: "auto",
            }}
          >
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              style={{ accentColor: DARK }}
            />
            {isPublic ? (
              <>
                <Globe size={13} /> 공개 (누구나 열람)
              </>
            ) : (
              <>
                <Lock size={13} color="#b06000" /> 비밀 (나·관리자만)
              </>
            )}
          </label>
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
          <button
            onClick={onCancel}
            style={{ ...ghostBtn, flex: 1, justifyContent: "center" }}
          >
            취소
          </button>
          <button
            onClick={submit}
            disabled={busy}
            style={{ ...primaryBtn, flex: 2 }}
          >
            {busy ? "등록 중…" : "문의 등록"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── 상세 스레드 ─────────────────────────────────
function InquiryThread({ groupId, onBack }) {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [replyAtt, setReplyAtt] = useState([]);
  const [busy, setBusy] = useState(false);
  // 문의 수정 (문의: "문의사항 등록 후 수정 기능 없음") — 내 글 + 답변 전만.
  const [editing, setEditing] = useState(false);
  const [eTitle, setETitle] = useState("");
  const [eContent, setEContent] = useState("");
  const [ePublic, setEPublic] = useState(false);
  const [eAtt, setEAtt] = useState([]); // 수정 중 유지할 첨부 목록 — ✕ 로 삭제(문의: 수정시 첨부 삭제 안 됨)

  const load = useCallback(() => {
    fetch(`/api/inquiry/thread/${groupId}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData({ error: "불러오기 실패" }));
  }, [groupId]);
  useEffect(load, [load]);

  const uploadReplyFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch("/api/inquiry/upload", {
      method: "POST",
      headers: authHeaders(),
      body: fd,
    });
    const d = await r.json();
    if (d.success) setReplyAtt((p) => [...p, d]);
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`/api/inquiry/${groupId}/reply`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ content: reply, attachments: replyAtt }),
      });
      const d = await r.json();
      if (d.success) {
        setReply("");
        setReplyAtt([]);
        load();
      } else alert(d.error || "답글 실패");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm("이 문의를 삭제할까요?")) return;
    const r = await fetch(`/api/inquiry/${groupId}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    const d = await r.json();
    if (d.success) onBack();
    else alert(d.error || "삭제 실패");
  };

  const startEdit = () => {
    const rootPost = data?.posts?.find((p) => p.depth === 0);
    if (!rootPost) return;
    setETitle(rootPost.title || "");
    setEContent(rootPost.content || "");
    setEPublic(!!rootPost.isPublic);
    setEAtt(rootPost.attachments || []);
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!eTitle.trim()) return alert("제목을 입력해주세요.");
    if (!eContent.trim()) return alert("내용을 입력해주세요.");
    setBusy(true);
    try {
      const r = await fetch(`/api/inquiry/${groupId}`, {
        method: "PUT",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          title: eTitle,
          content: eContent,
          isPublic: ePublic,
          attachments: eAtt, // 유지 목록 — 서버가 이 목록으로 재설정(삭제 반영)
        }),
      });
      const d = await r.json();
      if (d.success) {
        setEditing(false);
        load();
      } else alert(d.error || "수정 실패");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <Empty>불러오는 중…</Empty>;
  if (data.error) return <Empty>{data.error}</Empty>;

  const root = data.posts.find((p) => p.depth === 0);
  const canDelete = data.isAdmin || root?.isMine;
  // 수정 = 내 글 + 아직 답변 없음 (서버도 STATUS!=open 이면 409 로 거부)
  const hasReply = data.posts.some((p) => p.depth === 1);
  const canEdit = root?.isMine && !hasReply;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
        <button onClick={onBack} style={iconBtn}>
          <ChevronLeft size={18} />
        </button>
        <h1
          style={{
            fontSize: 18,
            fontWeight: 800,
            margin: 0,
            color: INK,
            flex: 1,
          }}
        >
          {data.hs6 || data.hsk ? "품목 의견" : "문의 상세"}
        </h1>
        {(data.hs6 || data.hsk) && (
          <span style={{ marginRight: 8 }}>
            <TopicBadge hs6={data.hs6} hsk={data.hsk} />
          </span>
        )}
        {canEdit && !editing && (
          <button onClick={startEdit} style={iconBtn} title="수정">
            <Pencil size={16} />
          </button>
        )}
        {canDelete && (
          <button
            onClick={del}
            style={{ ...iconBtn, color: "#dc2626" }}
            title="삭제"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      {data.posts.map((p) => (
        <div
          key={p.id}
          style={{
            ...card,
            padding: 18,
            marginBottom: 12,
            marginLeft: p.kind === "answer" ? 24 : 0,
            borderLeft:
              p.kind === "answer"
                ? `3px solid ${DARK}`
                : p.kind === "followup"
                  ? "3px solid #d97706"
                  : `1px solid ${LINE}`,
            background:
              p.kind === "answer"
                ? "#fafbfd"
                : p.kind === "followup"
                  ? "#fffbeb"
                  : "#fff",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
            }}
          >
            {p.kind === "answer" ? (
              <span style={{ ...miniTag, background: DARK, color: "#fff" }}>
                {p.aiAuthored ? "AI 답변" : "답변"}
              </span>
            ) : p.kind === "followup" ? (
              <span style={{ ...miniTag, background: "#d97706", color: "#fff" }}>
                추가질문
              </span>
            ) : p.isPublic ? (
              <Globe size={14} color={MUTED} />
            ) : (
              <Lock size={14} color="#b06000" />
            )}
            <span style={{ fontSize: 15, fontWeight: 700, color: INK }}>
              {p.kind === "question"
                ? p.title
                : p.kind === "answer"
                  ? p.aiAuthored
                    ? "AI 답변"
                    : "관리자 답변"
                  : "추가 질문"}
            </span>
            {p.draft && (
              <span
                data-testid="inquiry-answer-draft"
                style={{ ...miniTag, background: "#e5e7eb", color: INK }}
                title="아직 공개하지 않은 초안입니다. 문의자에게는 보이지 않습니다."
              >
                초안 · 공개 전
              </span>
            )}
            <span style={{ fontSize: 12, color: MUTED, marginLeft: "auto" }}>
              {p.author} · {fmtDate(p.createdAt)}
            </span>
          </div>
          {p.depth === 0 && editing ? (
            <>
              <input
                value={eTitle}
                onChange={(e) => setETitle(e.target.value)}
                placeholder="제목"
                style={input}
              />
              <textarea
                value={eContent}
                onChange={(e) => setEContent(e.target.value)}
                rows={8}
                style={{ ...input, resize: "vertical", lineHeight: 1.6 }}
              />
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                  color: "#374151",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={ePublic}
                  onChange={(e) => setEPublic(e.target.checked)}
                  style={{ accentColor: DARK }}
                />
                {ePublic ? (
                  <>
                    <Globe size={13} /> 공개 (누구나 열람)
                  </>
                ) : (
                  <>
                    <Lock size={13} color="#b06000" /> 비밀 (나·관리자만)
                  </>
                )}
              </label>
              {eAtt.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
                    첨부파일 — ✕ 를 누르면 저장 시 삭제됩니다
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {eAtt.map((a, i) => (
                      <span
                        key={i}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          padding: "3px 6px 3px 10px",
                          fontSize: 12,
                          background: "#f9fafb",
                          maxWidth: 260,
                        }}
                      >
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={a.name}
                        >
                          {a.name || "첨부"}
                        </span>
                        <button
                          onClick={() =>
                            setEAtt(eAtt.filter((_, j) => j !== i))
                          }
                          title="이 첨부 삭제"
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#b91c1c",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 700,
                            padding: "0 2px",
                            lineHeight: 1,
                          }}
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                <button
                  onClick={() => setEditing(false)}
                  style={{ ...ghostBtn, flex: 1, justifyContent: "center" }}
                >
                  취소
                </button>
                <button
                  onClick={saveEdit}
                  disabled={busy}
                  style={{ ...primaryBtn, flex: 2 }}
                >
                  {busy ? "저장 중…" : "수정 저장"}
                </button>
              </div>
            </>
          ) : p.masked ? (
            <div
              style={{
                fontSize: 13,
                color: MUTED,
                fontStyle: "italic",
                lineHeight: 1.6,
              }}
            >
              작성자가 비공개로 설정한 질문입니다 — 답변만 공개됩니다.
            </div>
          ) : (
            <div
              style={{
                fontSize: 14,
                color: "#374151",
                lineHeight: 1.7,
                whiteSpace: "pre-wrap",
              }}
            >
              {p.content}
            </div>
          )}
          {p.attachments.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginTop: 12,
              }}
            >
              {p.attachments.map((a, i) =>
                /image/.test(a.mime) ? (
                  <a key={i} href={a.url} target="_blank" rel="noreferrer">
                    <img
                      src={a.url}
                      alt=""
                      style={{
                        maxWidth: 220,
                        maxHeight: 220,
                        borderRadius: 8,
                        border: `1px solid ${LINE}`,
                      }}
                    />
                  </a>
                ) : (
                  <a
                    key={i}
                    href={a.url}
                    target="_blank"
                    rel="noreferrer"
                    style={ghostBtn}
                  >
                    <Paperclip size={13} /> {a.name}
                  </a>
                ),
              )}
            </div>
          )}
        </div>
      ))}

      {(data.isAdmin || data.isMine) && (
        <div style={{ ...card, padding: 16, marginTop: 4 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              marginBottom: 8,
              color: INK,
            }}
          >
            {data.isAdmin ? "관리자 답변" : "추가 질문"}
          </div>
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onPaste={(e) => {
              for (const it of e.clipboardData?.items || []) {
                if (it.type.startsWith("image/")) {
                  const f = it.getAsFile();
                  if (f) uploadReplyFile(f);
                }
              }
            }}
            placeholder={
              data.isAdmin
                ? "답변 입력 (Ctrl+V 로 캡처 붙여넣기 가능)"
                : "추가 질문을 입력하세요 (Ctrl+V 로 캡처 붙여넣기 가능)"
            }
            rows={4}
            style={{ ...input, resize: "vertical" }}
          />
          {replyAtt.length > 0 && (
            <div style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>
              첨부 {replyAtt.length}개
            </div>
          )}
          <button
            onClick={sendReply}
            disabled={busy}
            style={{ ...primaryBtn, width: "100%" }}
          >
            <Send
              size={14}
              style={{ marginRight: 6, verticalAlign: "middle" }}
            />
            {busy ? "등록 중…" : data.isAdmin ? "답변 등록" : "질문 등록"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── 공용 스타일 ─────────────────────────────────
const card = {
  background: "#fff",
  border: `1px solid ${LINE}`,
  borderRadius: 12,
  overflow: "hidden",
};
const input = {
  width: "100%",
  padding: "11px 13px",
  border: `1px solid #dadce0`,
  borderRadius: 10,
  fontSize: 14.5,
  outline: "none",
  boxSizing: "border-box",
  marginBottom: 12,
  fontFamily: "inherit",
};
const primaryBtn = {
  padding: "10px 18px",
  border: "none",
  borderRadius: 10,
  background: DARK,
  color: "#fff",
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
};
const ghostBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "8px 12px",
  border: `1px solid #dadce0`,
  borderRadius: 9,
  background: "#fff",
  fontSize: 13,
  color: "#374151",
  cursor: "pointer",
  textDecoration: "none",
};
const pageBtn = { ...ghostBtn, padding: "6px 14px" };
const iconBtn = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  padding: 6,
  marginRight: 4,
  color: INK,
  display: "inline-flex",
};
const miniTag = {
  fontSize: 10.5,
  fontWeight: 700,
  padding: "1px 6px",
  borderRadius: 999,
  background: "#f1f3f8",
  color: MUTED,
  marginLeft: 6,
};
const attChip = {
  position: "relative",
  border: `1px solid ${LINE}`,
  borderRadius: 8,
  background: "#fff",
  display: "flex",
  alignItems: "center",
};
const attDel = {
  position: "absolute",
  top: -7,
  right: -7,
  width: 18,
  height: 18,
  borderRadius: "50%",
  border: "none",
  background: "#1b1a20",
  color: "#fff",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
function Empty({ children }) {
  return (
    <div
      style={{
        padding: "40px 20px",
        textAlign: "center",
        color: MUTED,
        fontSize: 14,
      }}
    >
      {children}
    </div>
  );
}
